/**
 * Controls — the manual lever bank over the autonomous engine. Every lever
 * writes meta.system_state; the runners read the same keys each tick, so
 * changes apply within ~10 minutes and need no deploy. As sourcing/content
 * trust grows, the trust ladder hands these same levers to agents.
 */
import { useEffect, useState } from "react";
import { Chip, Empty, ErrorNote, Panel, Readout, useApi } from "../components/ui.js";
import { api } from "../lib/api.js";
import { fmtDate, int, n, pct, usd, PT_ZONE } from "../lib/format.js";
import { currentSubscription, disablePush, enablePush, pushSupportState } from "../lib/push.js";
import type { Num } from "../lib/types.js";

interface ControlsState {
  leadgen: {
    enabled: boolean;
    dailyTarget: number;
    batches: Array<{ id: string; run_date: string; cohort_key: string; title_band: string; industry_cohort: string; sourced_count: Num; kept_count: Num; status: string }>;
  };
  delivery: {
    dailySendCap: number;
    paused: boolean;
    window: { disabled: boolean; utcHours: number[]; utcDays: number[] };
  };
  forecast: {
    window: { start: string; end: string; hours: number; active: boolean } | null;
    dueByWindowEnd?: number;
    capRemaining?: number;
    projected?: number;
    limitedBy?: string;
  };
  cost: {
    budgets: Array<{ id: string; scope_kind: string; scope_key: string; period: string; budget_usd: Num; spent_usd: Num; hard_limit: boolean }>;
    spendTodayUsd: Num; spend7dUsd: Num; spend30dUsd: Num;
  };
  performance: {
    last30d: { sent: Num; delivered: Num; opened: Num; clicked: Num; human_opened: Num; human_clicked: Num; site_clicked: Num; human_site_clicked: Num; bounced: Num; unsubscribed: Num };
    outcomes: Array<{ status: string; contacts: Num }>;
  };
  replies: { calendarUrl: string | null };
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Render a UTC hour in Pacific time for display (the operator's timezone). */
function ptLabel(utcHour: number): string {
  const d = new Date(Date.UTC(2026, 5, 17, utcHour)); // a DST Wednesday
  return d.toLocaleTimeString("en-US", { hour: "numeric", timeZone: PT_ZONE });
}

function fmtWindow(w: { start: string; end: string }): string {
  const opts: Intl.DateTimeFormatOptions = { weekday: "short", hour: "numeric", timeZone: PT_ZONE };
  const s = new Date(w.start).toLocaleString("en-US", { ...opts, month: "short", day: "numeric" });
  const e = new Date(w.end).toLocaleTimeString("en-US", { hour: "numeric", timeZone: PT_ZONE });
  return `${s} – ${e} PT`;
}

export function Controls() {
  const ctl = useApi<ControlsState>("/api/controls");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<{ dailyTarget?: string; dailySendCap?: string; calendarUrl?: string }>({});
  const [linkCh, setLinkCh] = useState("linkedin");
  const [linkCampaign, setLinkCampaign] = useState("");
  const [copied, setCopied] = useState(false);

  const d = ctl.data;

  async function post(path: string, body: unknown) {
    setBusy(true);
    try { await api.post(path, body); ctl.reload(); } finally { setBusy(false); }
  }

  async function toggleLeadgen() {
    if (!d) return;
    const next = !d.leadgen.enabled;
    const msg = next
      ? `START autonomous lead sourcing?\n\n~${d.leadgen.dailyTarget} new leads/day from Apollo (spends credits). Every batch waits in Approvals for YOUR review before anyone is contacted.`
      : "STOP autonomous lead sourcing? In-flight batches stay in Approvals.";
    if (!window.confirm(msg)) return;
    await post("/api/controls/leadgen", { enabled: next });
  }

  function toggleHour(h: number) {
    if (!d) return;
    const hours = d.delivery.window.utcHours.includes(h)
      ? d.delivery.window.utcHours.filter((x) => x !== h)
      : [...d.delivery.window.utcHours, h].sort((a, b) => a - b);
    if (hours.length === 0) return; // never allow an empty window by accident
    void post("/api/controls/delivery", { utcHours: hours });
  }

  function toggleDay(day: number) {
    if (!d) return;
    const days = d.delivery.window.utcDays.includes(day)
      ? d.delivery.window.utcDays.filter((x) => x !== day)
      : [...d.delivery.window.utcDays, day].sort((a, b) => a - b);
    if (days.length === 0) return;
    void post("/api/controls/delivery", { utcDays: days });
  }

  return (
    <>
      <div className="page-head">
        <h1>Controls</h1>
        <span className="stamp">manual levers · runners re-read every tick (~10 min)</span>
      </div>

      {ctl.error ? <ErrorNote error={ctl.error} /> : null}

      {/* Forecast — the headline gauge of what's about to happen */}
      <Panel title="Next send window" alarm={d?.delivery.paused} delay={0}>
        {!d ? <Empty>loading…</Empty> : d.delivery.paused ? (
          <div className="row"><Chip tone="red">⏸ OUTBOUND PAUSED</Chip><span className="dim">kill switch is on (Agent Ops) — nothing sends regardless of window</span></div>
        ) : d.forecast.window ? (
          <div className="grid cols-4">
            <Readout label={d.forecast.window.active ? "Window OPEN now" : "Next window"} value={fmtWindow(d.forecast.window)} tone={d.forecast.window.active ? "green" : undefined} />
            <Readout hero label="Projected sends" value={int(d.forecast.projected ?? 0)} tone={n(d.forecast.projected) > 0 ? "green" : "dim"} note={`limited by ${d.forecast.limitedBy ?? "—"}`} />
            <Readout label="Due by window end" value={int(d.forecast.dueByWindowEnd ?? 0)} />
            <Readout label="Cap remaining that day" value={int(d.forecast.capRemaining ?? 0)} note={`of ${d.delivery.dailySendCap}/day`} />
          </div>
        ) : (
          <Empty>window config allows no sending — check hours/days below</Empty>
        )}
      </Panel>

      <div className="grid cols-2" style={{ marginTop: 14 }}>
        {/* Lead sourcing */}
        <Panel title="Lead sourcing — Apollo daily tick" delay={60} alarm={false}>
          {!d ? <Empty>loading…</Empty> : (
            <>
              <div className="killswitch" style={{ marginBottom: 14 }}>
                <div className={`ks-toggle ${d.leadgen.enabled ? "on" : ""}`} onClick={busy ? undefined : toggleLeadgen} role="switch" aria-checked={d.leadgen.enabled}>
                  <div className="lever">{d.leadgen.enabled ? "ON" : "OFF"}</div>
                </div>
                <div className="ks-status">
                  {d.leadgen.enabled
                    ? <span className="on" style={{ color: "var(--green)" }}>● sourcing ~{d.leadgen.dailyTarget}/day — every batch waits for your approval</span>
                    : <span className="dim">sourcing stopped — pipeline grows only from manual imports</span>}
                </div>
              </div>
              <div className="row" style={{ marginBottom: 14 }}>
                <label className="fld"><span>new leads / day (1–100)</span>
                  <input style={{ width: 90 }} value={draft.dailyTarget ?? String(d.leadgen.dailyTarget)}
                         onChange={(e) => setDraft({ ...draft, dailyTarget: e.target.value })} />
                </label>
                <button className="btn sm" disabled={busy || draft.dailyTarget === undefined}
                        style={{ alignSelf: "end" }}
                        onClick={() => { void post("/api/controls/leadgen", { dailyTarget: Number(draft.dailyTarget) }); setDraft({ ...draft, dailyTarget: undefined }); }}>
                  Set target
                </button>
                <span className="tag" style={{ alignSelf: "end" }}>rotates 1 title×industry cohort/day · USA-only ICP</span>
              </div>
              {d.leadgen.batches.length === 0 ? <Empty>no batches yet — they appear here and in Approvals</Empty> : (
                <table className="data">
                  <thead><tr><th>date</th><th>cohort</th><th>sourced</th><th>kept</th><th>status</th></tr></thead>
                  <tbody>
                    {d.leadgen.batches.map((b) => (
                      <tr key={b.id}>
                        <td className="num dim">{fmtDate(b.run_date)}</td>
                        <td>{b.title_band} × {b.industry_cohort}</td>
                        <td className="num">{int(b.sourced_count)}</td>
                        <td className="num">{int(b.kept_count)}</td>
                        <td><Chip tone={b.status === "pending_review" ? "amber" : b.status === "enrolled" ? "green" : undefined}>{b.status}</Chip></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </Panel>

        {/* Delivery */}
        <Panel title="Delivery — cap + send windows" delay={120}>
          {!d ? <Empty>loading…</Empty> : (
            <>
              <div className="row" style={{ marginBottom: 14 }}>
                <label className="fld"><span>emails / day cap</span>
                  <input style={{ width: 90 }} value={draft.dailySendCap ?? String(d.delivery.dailySendCap)}
                         onChange={(e) => setDraft({ ...draft, dailySendCap: e.target.value })} />
                </label>
                <button className="btn sm" disabled={busy || draft.dailySendCap === undefined}
                        style={{ alignSelf: "end" }}
                        onClick={() => { void post("/api/controls/delivery", { dailySendCap: Number(draft.dailySendCap) }); setDraft({ ...draft, dailySendCap: undefined }); }}>
                  Set cap
                </button>
                <span className="tag" style={{ alignSelf: "end" }}>warmup discipline — raise gradually</span>
              </div>

              <div className="tag" style={{ marginBottom: 6 }}>send days (Pacific)</div>
              <div className="row" style={{ marginBottom: 12 }}>
                {DAY_LABELS.map((label, day) => (
                  <button key={day} className={`btn sm ${d.delivery.window.utcDays.includes(day) ? "ghost-green" : ""}`}
                          disabled={busy || d.delivery.window.disabled} onClick={() => toggleDay(day)}>
                    {label}
                  </button>
                ))}
              </div>

              <div className="tag" style={{ marginBottom: 6 }}>send hours (shown in PT · stored UTC)</div>
              <div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
                {Array.from({ length: 24 }, (_, h) => (
                  <button key={h} className={`btn sm ${d.delivery.window.utcHours.includes(h) ? "ghost-green" : ""}`}
                          disabled={busy || d.delivery.window.disabled}
                          title={`${h}:00 UTC`} onClick={() => toggleHour(h)}>
                    {ptLabel(h)}
                  </button>
                ))}
              </div>
              {d.delivery.window.disabled ? (
                <div className="tag" style={{ marginTop: 8, color: "var(--amber)" }}>window DISABLED — sends fire on any tick</div>
              ) : null}
            </>
          )}
        </Panel>
      </div>

      <Panel title="Channel link builder — tracked install links for any surface" delay={150}>
        <div className="row">
          <label className="fld"><span>channel</span>
            <select value={linkCh} onChange={(e) => setLinkCh(e.target.value)}>
              {["linkedin", "youtube", "web", "influencer", "email"].map((ch) => <option key={ch}>{ch}</option>)}
            </select>
          </label>
          <label className="fld" style={{ flex: 1 }}><span>campaign slug (e.g. video-sop-basics, post-2026-06)</span>
            <input value={linkCampaign} onChange={(e) => { setLinkCampaign(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, "-")); setCopied(false); }} />
          </label>
        </div>
        <div className="row" style={{ marginTop: 10 }}>
          <code className="mono" style={{ fontSize: 14, padding: "6px 10px", background: "var(--panel-2)", border: "1px solid var(--line)", flex: 1 }}>
            {`https://turboproc.com/go?ch=${linkCh}${linkCampaign ? `&c=${linkCampaign}` : ""}`}
          </code>
          <button className="btn sm" onClick={() => { void navigator.clipboard.writeText(`https://turboproc.com/go?ch=${linkCh}${linkCampaign ? `&c=${linkCampaign}` : ""}`); setCopied(true); }}>
            {copied ? "✓ copied" : "Copy"}
          </button>
        </div>
        <div className="tag" style={{ marginTop: 8 }}>
          paste into LinkedIn posts, YouTube descriptions, site buttons — clicks route through the /go redirect (Apple App Analytics campaign link); installs + subscriptions attribute to this channel/campaign (Revenue → Attribution)
        </div>
      </Panel>

      <PushAlertsPanel />

      <Panel title="Reply drafts — calendar booking link" delay={165}>
        <div className="row">
          <label className="fld" style={{ flex: 1 }}><span>booking URL offered in qualified reply drafts (https://…) — leave empty to have drafts invite a reply instead</span>
            <input
              placeholder="https://cal.com/your-link/15min"
              value={draft.calendarUrl ?? d?.replies?.calendarUrl ?? ""}
              onChange={(e) => setDraft({ ...draft, calendarUrl: e.target.value })}
            />
          </label>
          <button className="btn sm" disabled={busy || draft.calendarUrl === undefined}
                  onClick={() => { void post("/api/controls/replies", { calendarUrl: (draft.calendarUrl ?? "").trim() }); setDraft({ ...draft, calendarUrl: undefined }); }}>
            Save
          </button>
        </div>
        <div className="tag" style={{ marginTop: 8 }}>
          when a prospect replies wanting to talk, the drafted response (waiting in Approvals) offers this link · nothing sends without your approval
        </div>
      </Panel>

      <div className="grid cols-2" style={{ marginTop: 14 }}>
        {/* Cost */}
        <Panel title="Cost tracking" delay={180} alarm={d ? d.cost.budgets.length === 0 : false}>
          {!d ? <Empty>loading…</Empty> : (
            <>
              <div className="grid cols-3" style={{ marginBottom: 12 }}>
                <Readout label="LLM spend · today" value={usd(d.cost.spendTodayUsd)} />
                <Readout label="7 days" value={usd(d.cost.spend7dUsd)} />
                <Readout label="30 days" value={usd(d.cost.spend30dUsd)} />
              </div>
              {d.cost.budgets.length === 0 ? <Empty>no budget caps — enforcement off (set one in Agent Ops)</Empty> : (
                d.cost.budgets.map((b) => {
                  const used = n(b.spent_usd) / Math.max(n(b.budget_usd), 1);
                  return (
                    <div key={b.id} style={{ marginBottom: 10 }}>
                      <div className="row" style={{ justifyContent: "space-between" }}>
                        <span className="tag">{b.scope_kind}{b.scope_key ? ` · ${b.scope_key}` : ""} · {b.period}{b.hard_limit ? " · hard" : ""}</span>
                        <span className="mono" style={{ fontSize: 14 }}>{usd(b.spent_usd)} / {usd(b.budget_usd)} ({pct(used, 0)})</span>
                      </div>
                      <div style={{ height: 6, background: "var(--panel-2)", border: "1px solid var(--line)" }}>
                        <div style={{ height: "100%", width: `${Math.min(used * 100, 100)}%`, background: used > 0.8 ? "var(--red)" : "var(--green)" }} />
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}
        </Panel>

        {/* Performance */}
        <Panel title="Performance · trailing 30 days" delay={240}>
          {!d ? <Empty>loading…</Empty> : (
            <>
              <div className="grid cols-4" style={{ marginBottom: 12 }}>
                <Readout label="Sent" value={int(d.performance.last30d?.sent)} />
                <Readout label="Opened · human" value={int(d.performance.last30d?.human_opened)}
                         note={n(d.performance.last30d?.sent) > 0 ? `${pct(n(d.performance.last30d?.human_opened) / n(d.performance.last30d?.sent))} · ${int(d.performance.last30d?.opened)} raw` : undefined} />
                <Readout label="App Store CTA · human" value={int(d.performance.last30d?.human_clicked)}
                         note={n(d.performance.last30d?.sent) > 0 ? `${pct(n(d.performance.last30d?.human_clicked) / n(d.performance.last30d?.sent))} · ${int(d.performance.last30d?.clicked)} raw` : undefined} />
                <Readout label="Bounced" value={int(d.performance.last30d?.bounced)} tone={n(d.performance.last30d?.bounced) > 0 ? "amber" : "green"} />
              </div>
              <div className="tag" style={{ marginBottom: 4 }}>
                “human” = bot-filtered (excludes Apple-MPP / image-proxy / security-scanner machine events); “raw” is the unfiltered Postmark count.
              </div>
              <div className="tag" style={{ marginBottom: 4 }}>
                Learn-more CTA · human · 30d: <strong>{int(d.performance.last30d?.human_site_clicked)}</strong>
                <span className="dim"> ({int(d.performance.last30d?.site_clicked)} raw)</span> — the secondary, research-intent CTA (direct site link), separate from the App Store CTA above.
              </div>
              <div className="row">
                {d.performance.outcomes.map((o) => (
                  <Chip key={o.status} tone={o.status === "paying" ? "green" : "blue"}>{o.status}: {int(o.contacts)}</Chip>
                ))}
                {d.performance.outcomes.length === 0 ? <span className="dim">no replied/installed/paying contacts yet</span> : null}
              </div>
            </>
          )}
        </Panel>
      </div>
    </>
  );
}

/** Phone alerts — web-push enrollment for THIS device + a test button. */
function PushAlertsPanel() {
  const cfg = useApi<{ vapidPublicKey: string | null; configured: boolean; subscriptions: Array<{ id: string; label: string | null; created_at: string; last_used_at: string | null }> }>("/api/push/config");
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const support = pushSupportState();

  useEffect(() => {
    void currentSubscription().then((s) => setSubscribed(Boolean(s)));
  }, []);

  async function toggle() {
    if (!cfg.data?.vapidPublicKey) return;
    setBusy(true);
    setNote(null);
    try {
      if (subscribed) {
        await disablePush();
        setSubscribed(false);
        setNote("this device unsubscribed");
      } else {
        await enablePush(cfg.data.vapidPublicKey);
        setSubscribed(true);
        setNote("subscribed — try Send test");
      }
      cfg.reload();
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    setBusy(true);
    setNote(null);
    try {
      await api.post("/api/push/test", {});
      setNote("test sent — check your notifications");
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Phone alerts — web push to this device" delay={158}>
      {!cfg.data ? <Empty>loading…</Empty> : !cfg.data.configured ? (
        <div className="tag" style={{ color: "var(--amber)" }}>VAPID keys not configured on the API worker yet</div>
      ) : support === "needs-install" ? (
        <div className="tag">
          install the dashboard first: tap the share button, then "Add to Home Screen", then open it from there and come back to this panel
        </div>
      ) : support === "unsupported" ? (
        <div className="tag">this browser does not support web push</div>
      ) : (
        <>
          <div className="row">
            <button className="btn sm" disabled={busy || subscribed === null} onClick={() => void toggle()}>
              {subscribed ? "Disable on this device" : "Enable alerts on this device"}
            </button>
            <button className="btn sm" disabled={busy || !subscribed} onClick={() => void sendTest()}>Send test</button>
            {note ? <span className="tag" style={{ color: "var(--green)" }}>{note}</span> : null}
          </div>
          <div className="tag" style={{ marginTop: 8 }}>
            hot leads (qualified replies) and critical system alerts arrive as notifications · devices enrolled: {cfg.data.subscriptions.length}
            {cfg.data.subscriptions.length > 0 ? ` (${cfg.data.subscriptions.map((s) => s.label ?? "device").join(", ")})` : ""}
          </div>
        </>
      )}
    </Panel>
  );
}
